import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useDeleteAccountMutation } from "../services/appApi";
import "./DeleteAccount.css";

function DeleteAccount() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [deleteAccount, { isLoading }] = useDeleteAccountMutation();
  const [password, setPassword] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  if (!user) {
    return (
      <div className="delete-acc-root">
        <div className="delete-acc-card" style={{ textAlign: "center" }}>
          <div className="delete-acc-icon-wrap" style={{ background: "linear-gradient(135deg, #64748b 0%, #475569 100%)", boxShadow: "none" }}>
            🗑️
          </div>
          <h1 className="delete-acc-title">Delete Your Munazem Account</h1>
          <p className="delete-acc-subtitle" style={{ color: "#94a3b8", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            To permanently delete your account and all associated data, you must first log in to verify your identity.
          </p>
          <button 
            onClick={() => navigate("/login", { state: { from: "/deleteaccount" } })}
            className="delete-acc-btn"
            style={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", boxShadow: "0 4px 20px rgba(99, 102, 241, 0.4)", width: "100%", border: "none" }}
          >
            Log In to Delete Account
          </button>
          <p className="delete-acc-alternative" style={{ color: "#94a3b8", fontSize: "14px", marginTop: "20px", lineHeight: "1.6", background: "rgba(255,255,255,0.02)", padding: "12px", borderRadius: "6px", border: "1px dashed rgba(255,255,255,0.1)" }}>
            If you no longer have the app or cannot log in, please request account deletion by emailing us at <a href="mailto:munazemteam@munazem.com" style={{ color: "#818cf8", textDecoration: "underline" }}>munazemteam@munazem.com</a> from your registered email address. We will verify your identity via email and process your request within 7 business days.
          </p>
          <div className="delete-acc-footer" style={{ marginTop: "20px" }}>
            <Link to="/" className="delete-acc-link" style={{ color: "#818cf8" }}>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!password) {
      setErrorMsg("Please enter your password to confirm deletion.");
      return;
    }

    if (!confirmChecked) {
      setErrorMsg("You must check the confirmation box to proceed.");
      return;
    }

    try {
      const result = await deleteAccount({ password }).unwrap();
      
      if (result.success) {
        // Clean up token and persistence storage
        localStorage.removeItem("userToken");
        localStorage.removeItem("persist:root");

        alert("Your account has been successfully deleted. We're sad to see you go.");
        
        // Redirect to homepage
        window.location.replace("/");
      }
    } catch (err) {
      const msg = err?.data?.error || err?.data?.message || "Incorrect password or server error. Account deletion cancelled.";
      setErrorMsg(msg);
    }
  };

  return (
    <div className="delete-acc-root">
      <div className="delete-acc-card">
        {/* Warning Icon */}
        <div className="delete-acc-icon-wrap" aria-hidden="true">
          ⚠️
        </div>

        <h1 className="delete-acc-title">Delete Account</h1>
        <p className="delete-acc-subtitle">
          Warning: This action is permanent and irreversible. All of your personal
          information, event details, and notification history will be erased from our database.
        </p>

        {/* Error Alert */}
        {errorMsg && (
          <div className="delete-acc-error" role="alert">
            <span aria-hidden="true">⚠️</span> {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Password field */}
          <div className="delete-acc-input-wrap">
            <input
              id="delete-password"
              type="password"
              className="delete-acc-input"
              placeholder="Confirm your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          {/* Confirmation Checkbox */}
          <label className="delete-acc-checkbox-wrap">
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              disabled={isLoading}
            />
            <span>
              I understand that this action is permanent and my account details
              cannot be recovered.
            </span>
          </label>

          {/* Delete Button */}
          <button
            type="submit"
            className="delete-acc-btn"
            disabled={isLoading || !password || !confirmChecked}
          >
            {isLoading ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                Deleting Account…
              </>
            ) : (
              "Permanently Delete Account"
            )}
          </button>
        </form>

        <div className="delete-acc-footer">
          Changed your mind?{" "}
          <Link to="/profile" className="delete-acc-link">
            Back to Profile
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DeleteAccount;
